import React, { useState, type ReactNode } from 'react';

/**
 * The configuration for a single tab in the inspector accordeon.
 */
export interface InspectorTab {
  /** A unique key for the tab. */
  key: string;
  /** The title for the tab. */
  title: string;
  /** The rendered content of the tab. */
  content: ReactNode;
}

interface InspectorTabsProps {
  /** The configuration for the tabs. */
  tabs: InspectorTab[];
}

/**
 * Render the accordeon tabs in the inspector.
 *
 * Props:
 * - {Array[Object]} tabs: the configuration for the tabs. Each tabs needs:
 *   - key: a unique key for the tab
 *   - title: the title for the tab
 *   - content: the rendered content
 */
export default function InspectorTabs({ tabs }: InspectorTabsProps) {
  // Initial active tab is the first tab's key (constructor init -> useState
  // initializer). `tabs` is stable for a given inspector mount, matching the
  // class which only read tabs[0].key once in the constructor.
  const [tab, setTab] = useState(tabs[0].key);

  const renderTab = (t: InspectorTab) => {
    return (
      <div
        key={t.key}
        className={`tab ${t.key} ${tab === t.key ? 'active' : ''}`}
        onClick={() => {
          setTab(t.key);
        }}
      >
        <div className="tab-title">{t.title}</div>
        <div className="tab-content">{t.content}</div>
      </div>
    );
  };

  return (
    <div className="tab-wrapper flex flex-1 flex-col overflow-auto">
      {tabs.map((t) => renderTab(t))}
    </div>
  );
}
